import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getMyStartup, getQitDefinition, getQitResponses, getQitYears,
  prefillQit, saveQitResponses, submitQit, getQitSubmissionStatus,
  getBpStatus,
} from '../../api';
import { useToast } from '../../components/Toast';
import { migrateLegacyReferral } from '../../utils/referralSources';

const CURRENT_YEAR = 2026;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const normalize = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

// Compute months of existence from annee_creation (Jan 1 of that year)
function monthsOfExistence(anneeCreation) {
  if (!anneeCreation) return '';
  const year = parseInt(anneeCreation, 10);
  if (!year || year < 1900) return '';
  const start = new Date(year, 0, 1);
  const now = new Date();
  const months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  return Math.max(0, months).toString();
}

// Resolve prefill source value from the startup record
function resolvePrefill(prefillKey, startup) {
  if (!prefillKey || !startup) return '';
  if (prefillKey === 'annee_creation_months') return monthsOfExistence(startup.annee_creation);
  if (prefillKey === 'contact_name') {
    return startup.contact_name || [startup.first_name, startup.last_name].filter(Boolean).join(' ') || '';
  }
  if (prefillKey === 'referral_source') {
    // Legacy short codes (presse / internet / forums_salons / demarchage / autre)
    // are migrated to their human-readable equivalent so they match the QIT
    // multiselect options.
    return migrateLegacyReferral(startup.referral_source || '');
  }
  const val = startup[prefillKey];
  return (val === null || val === undefined) ? '' : String(val);
}

// Evaluate depends_on rule against current responses map (cluster_id_question_id keys)
function isVisible(question, responsesByQid) {
  const dep = question.depends_on;
  if (!dep) return true;
  const parentVal = responsesByQid[dep.question_id];
  if (parentVal === undefined || parentVal === null || parentVal === '') return false;
  if (dep.value !== undefined) return parentVal === dep.value;
  if (dep.value_in) return dep.value_in.includes(parentVal);
  if (dep.value_not_in) return !dep.value_not_in.includes(parentVal);
  return true;
}

// Split a multiselect_other stored value into { selected: [...], other: 'text' }
function parseMultiselectOther(value) {
  const parts = (value || '').split(',').map(s => s.trim()).filter(Boolean);
  const selected = [];
  let other = '';
  parts.forEach(p => {
    if (p.startsWith('Other:')) other = p.substring(6).trim();
    else if (p.startsWith('Autre:')) other = p.substring(6).trim();
    else selected.push(p);
  });
  return { selected, other };
}
function joinMultiselectOther(selected, other) {
  const items = [...selected];
  if (other && other.trim()) items.push('Other:' + other.trim());
  return items.join(',');
}

// Reconcile loaded responses with the *current* QIT_DEFINITION.
//
// Why this matters : option lists evolve between QIT versions. Without
// reconciliation, a saved value that no longer matches a current option
// stays in the response map but is invisible in the UI (multiselect /
// multiselect_other) or shown as "(ancienne réponse)" (select). The
// startup might think their previous answer was lost.
//
// Strategy :
//   - select          : exact match → fuzzy match (NFD-normalized) → keep
//   - multiselect     : exact match → legacy referral map → drop unknowns silently
//   - multiselect_other : exact match → legacy referral map → fall back to
//                          the "Autre (préciser)" free-text field so the
//                          user keeps visibility on their original input
function reconcileResponses(map, definition) {
  if (!definition) return map;
  const reconciled = { ...map };
  Object.entries(reconciled).forEach(([key, value]) => {
    if (!value) return;
    const idx = key.indexOf('_');
    if (idx < 0) return;
    const clusterId = key.substring(0, idx);
    const questionId = key.substring(idx + 1);
    const cluster = definition[clusterId];
    if (!cluster) return;
    const question = cluster.questions.find(q => q.id === questionId);
    if (!question || !question.options) return;

    // ----- select : fuzzy-match against current options -----
    if (question.type === 'select') {
      if (question.options.includes(value)) return;
      const normalizedValue = normalize(value);
      const match = question.options.find(o => normalize(o) === normalizedValue);
      if (match) reconciled[key] = match;
      return;
    }

    // ----- multiselect / multiselect_other : token-by-token reconcile -----
    if (question.type === 'multiselect' || question.type === 'multiselect_other') {
      const opts = question.options;
      const tokens = String(value).split(',').map(t => t.trim()).filter(Boolean);
      const fixed = [];        // tokens kept (in current options or Other:/Autre: prefixed)
      const orphans = [];      // tokens that no longer match any option

      tokens.forEach(t => {
        if (t.startsWith('Other:') || t.startsWith('Autre:')) {
          fixed.push(t);
          return;
        }
        if (opts.includes(t)) {
          fixed.push(t);
          return;
        }
        // Try legacy referral migration (only effective for question 16-style
        // referral-source tokens — a no-op on unrelated values).
        const migrated = migrateLegacyReferral(t);
        if (migrated !== t && opts.includes(migrated)) {
          fixed.push(migrated);
          return;
        }
        // Try fuzzy NFD match against current options
        const normalized = normalize(t);
        const fuzzy = opts.find(o => normalize(o) === normalized);
        if (fuzzy) {
          fixed.push(fuzzy);
          return;
        }
        orphans.push(t);
      });

      // For multiselect_other, surface orphan tokens in the "Autre" free-text
      // field so the candidate keeps visibility on their original input.
      if (question.type === 'multiselect_other' && orphans.length > 0) {
        const otherIdx = fixed.findIndex(t => t.startsWith('Other:') || t.startsWith('Autre:'));
        if (otherIdx >= 0) {
          const prefix = fixed[otherIdx].startsWith('Other:') ? 'Other:' : 'Autre:';
          const existing = fixed[otherIdx].substring(prefix.length).trim();
          fixed[otherIdx] = prefix + (existing ? `${existing} ${orphans.join(' ')}` : orphans.join(' '));
        } else {
          fixed.push('Other:' + orphans.join(' '));
        }
      }
      // For plain multiselect, orphans are dropped silently (they have no
      // free-text fallback). The user can re-save fresh selections.

      const newValue = fixed.join(',');
      if (newValue !== value) reconciled[key] = newValue;
    }
  });
  return reconciled;
}

// Build a flat map { questionId: value } for the *current cluster* — used for skip-logic
function buildQidLookup(responses, clusterId, cluster) {
  const lookup = {};
  if (!cluster) return lookup;
  cluster.questions.forEach(q => {
    lookup[q.id] = responses[`${clusterId}_${q.id}`] || '';
  });
  return lookup;
}

// Compute a display ID for the startup-facing UI based on cluster position.
// CL-IDENT : pas de numérotation (questions hétérogènes issues de §2/§3/§16).
// CL-01..CL-12 : <numéro_cluster><lettre>, ex. CL-01 / 1ère question -> "1a".
//   Les questions _bp (Business Plan attachment) deviennent "1_bp" et ne
//   consomment pas de lettre dans la séquence a, b, c...
function computeDisplayId(clusterId, questionId, indexInCluster, clusterQuestions) {
  if (clusterId === 'CL-IDENT') return null;
  const m = /^CL-(\d+)$/.exec(clusterId);
  if (!m) return null;
  const clusterNum = parseInt(m[1], 10);
  if (questionId.endsWith('_bp')) return `${clusterNum}_bp`;
  // Count letter slot, skipping _bp questions before this index
  let letterIdx = 0;
  for (let i = 0; i < indexInCluster; i++) {
    if (!clusterQuestions[i].id.endsWith('_bp')) letterIdx++;
  }
  return `${clusterNum}${String.fromCharCode(97 + letterIdx)}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function QitPage() {
  const toast = useToast();
  const [startup, setStartup] = useState(null);
  const [definition, setDefinition] = useState(null);
  const [responses, setResponses] = useState({});
  const [activeCluster, setActiveCluster] = useState('CL-IDENT');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [qitYear, setQitYear] = useState(CURRENT_YEAR);
  const [availableYears, setAvailableYears] = useState([]);
  const [prefilling, setPrefilling] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [qitSubmittedForYear, setQitSubmittedForYear] = useState(false);
  const [bpStatus, setBpStatus] = useState({ uploaded: false, file_name: null });
  // Certification modal — opened on submit, blocks the submit until the
  // startup explicitly accepts (checkbox + "Confirmer") or refuses ("Refuser").
  // Both decisions are tracked server-side via startup_events.
  const [certModalOpen, setCertModalOpen] = useState(false);
  const [certChecked, setCertChecked] = useState(false);
  const [certBusy, setCertBusy] = useState(false);

  const refreshBpStatus = async () => {
    try { setBpStatus(await getBpStatus()); } catch { /* ignore */ }
  };

  useEffect(() => {
    Promise.all([getMyStartup(), getQitDefinition(), getBpStatus().catch(() => ({ uploaded: false }))])
      .then(async ([s, def, bp]) => {
        setStartup(s);
        setDefinition(def);
        setBpStatus(bp);

        const yearsData = await getQitYears(s.id);
        const years = yearsData.years || yearsData || [];
        setAvailableYears(years);

        const hasCurrent = years.some(y => parseInt(y.year) === CURRENT_YEAR);
        const hasPrev = years.some(y => parseInt(y.year) === CURRENT_YEAR - 1);
        let yearToLoad = CURRENT_YEAR;
        let isViewMode = false;
        if (hasCurrent) { yearToLoad = CURRENT_YEAR; isViewMode = false; }
        else if (hasPrev) { yearToLoad = CURRENT_YEAR - 1; isViewMode = true; }
        setQitYear(yearToLoad);
        setViewMode(isViewMode);

        try {
          const subStatus = await getQitSubmissionStatus(s.id, yearToLoad);
          if (subStatus.submitted) { setQitSubmittedForYear(true); setViewMode(true); }
        } catch { /* ignore */ }

        const existing = await getQitResponses(s.id, yearToLoad);
        const map = {};
        if (existing && existing.responses) {
          Object.entries(existing.responses).forEach(([clusterId, questions]) => {
            questions.forEach(r => { map[`${clusterId}_${r.question_id}`] = r.declared_value; });
          });
        }
        // Auto-prefill from startup profile for prefill_from questions that have no answer yet
        Object.entries(def).forEach(([clusterId, cluster]) => {
          cluster.questions.forEach(q => {
            if (!q.prefill_from) return;
            const key = `${clusterId}_${q.id}`;
            if (map[key]) return; // already answered
            const val = resolvePrefill(q.prefill_from, s);
            if (val) map[key] = val;
          });
        });
        setResponses(reconcileResponses(map, def));
      })
      .catch(console.error);
  }, []); // eslint-disable-line

  const loadYear = async (year, isViewModeArg) => {
    if (!startup) return;
    setQitYear(year);
    setQitSubmittedForYear(false);
    try {
      const subStatus = await getQitSubmissionStatus(startup.id, year);
      if (subStatus.submitted) { setQitSubmittedForYear(true); setViewMode(true); }
      else { setViewMode(isViewModeArg); }
      const existing = await getQitResponses(startup.id, year);
      const map = {};
      if (existing && existing.responses) {
        Object.entries(existing.responses).forEach(([clusterId, questions]) => {
          questions.forEach(r => { map[`${clusterId}_${r.question_id}`] = r.declared_value; });
        });
      }
      // Re-apply prefill for empty fields
      if (definition && startup) {
        Object.entries(definition).forEach(([clusterId, cluster]) => {
          cluster.questions.forEach(q => {
            if (!q.prefill_from) return;
            const key = `${clusterId}_${q.id}`;
            if (map[key]) return;
            const val = resolvePrefill(q.prefill_from, startup);
            if (val) map[key] = val;
          });
        });
      }
      setResponses(reconcileResponses(map, definition));
    } catch (err) { console.error(err); }
  };

  const handlePrefill = async () => {
    if (!startup) return;
    const ok = await toast.confirm(
      `Pré-remplir le QIT ${CURRENT_YEAR} avec les réponses de ${CURRENT_YEAR - 1} ?\nVous pourrez ensuite modifier les réponses.`
    );
    if (!ok) return;
    setPrefilling(true);
    try {
      await prefillQit(startup.id, CURRENT_YEAR - 1, CURRENT_YEAR);
      await loadYear(CURRENT_YEAR, false);
      const yearsData = await getQitYears(startup.id);
      setAvailableYears(yearsData.years || yearsData || []);
    } catch (err) { toast.error(err.message); }
    setPrefilling(false);
  };

  const startFreshQit = () => {
    setQitYear(CURRENT_YEAR);
    setViewMode(false);
    // Re-prefill from startup profile only
    if (definition && startup) {
      const map = {};
      Object.entries(definition).forEach(([clusterId, cluster]) => {
        cluster.questions.forEach(q => {
          if (!q.prefill_from) return;
          const val = resolvePrefill(q.prefill_from, startup);
          if (val) map[`${clusterId}_${q.id}`] = val;
        });
      });
      setResponses(map);
    } else {
      setResponses({});
    }
  };

  const handleChange = (clusterId, questionId, value) => {
    setResponses(r => ({ ...r, [`${clusterId}_${questionId}`]: value }));
    setSaved(false);
  };

  // Raw count of any non-empty response (regardless of visibility) — used to
  // gate the "Sauvegarder" button. Hidden answers (skip-logic) still need to
  // be persisted in case they become visible again later.
  const rawAnsweredCount = Object.values(responses).filter(v => v !== undefined && v !== null && v !== '').length;

  const handleSave = async () => {
    if (!startup || viewMode) return;
    if (rawAnsweredCount === 0) {
      toast.error('Aucune question renseignée. Veuillez répondre à au moins une question avant de sauvegarder.');
      return;
    }
    setSaving(true);
    const data = Object.entries(responses)
      .filter(([_, v]) => v !== undefined && v !== null && v !== '')
      .map(([key, value]) => {
        const idx = key.indexOf('_');
        const cluster_id = key.substring(0, idx);
        const question_id = key.substring(idx + 1);
        return { cluster_id, question_id, value };
      });
    try {
      await saveQitResponses(startup.id, data, qitYear);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  // Pre-flight checks before opening the certification modal.
  // The modal itself triggers the actual submit on user confirmation.
  const handleSubmit = async () => {
    if (!startup || viewMode) return;
    if (!startup.trophy_participant) {
      toast.error('Vous devez participer aux Trophées de l\'Innovation pour soumettre le questionnaire.');
      return;
    }
    if (!bpStatus.uploaded) {
      toast.error('Le Business Plan doit être déposé dans la Data Room (catégorie « Business Plan ») avant la soumission.');
      return;
    }
    // Use the effective counts (skip-logic + BP validation aware) so the
    // 80%-threshold matches what the user sees in the progress bar.
    const total = totalQuestions;
    const answered = answeredCount;
    const pct = globalProgress;
    if (pct === 0) { toast.error('Le questionnaire est vide.'); return; }
    if (pct < 80) {
      await toast.confirm(`Le questionnaire n'est rempli qu'à ${pct}% (${answered}/${total} questions). Minimum 80% requis pour soumettre.`);
      return;
    }
    // All checks passed — open the certification modal. The modal handles
    // both branches (accept / decline) and calls submitQit with the proper flag.
    setCertChecked(false);
    setCertModalOpen(true);
  };

  // Called from the certification modal. accepted=true submits the dossier ;
  // accepted=false records the refusal but does NOT submit. Both branches log
  // a startup_event server-side for the admin audit trail.
  const handleCertificationDecision = async (accepted) => {
    if (!startup || certBusy) return;
    if (accepted && !certChecked) {
      toast.error('Veuillez cocher la case de certification pour confirmer.');
      return;
    }
    setCertBusy(true);
    try {
      // Save current answers before submitting (only on accept — no point on refuse)
      if (accepted) await handleSave();
      const res = await submitQit(startup.id, qitYear, accepted);
      setCertModalOpen(false);
      if (res && res.submitted) {
        setSubmitted(true);
      } else {
        // Refusal path — keep the page open, show a clear message
        toast.error('Certification refusée. Le dossier n\'a pas été soumis. La date et le motif ont été enregistrés.');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCertBusy(false);
    }
  };

  // ----- derived state -----

  const clusters = useMemo(() => definition ? Object.entries(definition) : [], [definition]);
  const current = definition ? definition[activeCluster] : null;
  const currentIdx = clusters.findIndex(([id]) => id === activeCluster);
  const hasPreviousYear = availableYears.some(y => parseInt(y.year) === CURRENT_YEAR - 1);
  const hasCurrentYear = availableYears.some(y => parseInt(y.year) === CURRENT_YEAR);

  // ---------------------------------------------------------------------
  // Per-cluster effective counts — take skip-logic AND BP validation into
  // account. A cluster is at 100% when all *visible* questions are answered ;
  // questions hidden by skip-logic don't count in the denominator.
  //
  // CL-01 (Business Plan) special case: the "5_bp" pseudo-question (type
  // 'attachment_status') is considered answered only when the Business Plan
  // has been *validated* by the admin (bpStatus.status === 'valide').
  // ---------------------------------------------------------------------
  const isQuestionAnswered = (clusterId, q) => {
    if (q.type === 'attachment_status' && q.id === '5_bp') {
      return bpStatus.status === 'valide';
    }
    const v = responses[`${clusterId}_${q.id}`];
    return v !== undefined && v !== null && v !== '';
  };

  // "Commentaires éventuels" questions are optional by design (the very label
  // says so). They count in the numerator if filled, but never in the
  // denominator — so a cluster can reach 100% without forcing the startup
  // to leave a comment in every section. Detected via the question text
  // prefix to keep the rule visible/auditable on the spot.
  const isOptionalQuestion = (q) => {
    const t = (q.text || '').trim().toLowerCase();
    return t.startsWith('commentaires éventuels') || t.startsWith('commentaires eventuels');
  };

  const getClusterCounts = (clusterId) => {
    if (!definition) return { visible: 0, required: 0, answered: 0, total: 0, optionalAnswered: 0 };
    const cl = definition[clusterId];
    if (!cl) return { visible: 0, required: 0, answered: 0, total: 0, optionalAnswered: 0 };
    const lookup = buildQidLookup(responses, clusterId, cl);
    const visibleQs = cl.questions.filter(q => isVisible(q, lookup));
    const requiredQs = visibleQs.filter(q => !isOptionalQuestion(q));
    const optionalQs = visibleQs.filter(q => isOptionalQuestion(q));
    const answered = requiredQs.filter(q => isQuestionAnswered(clusterId, q)).length;
    const optionalAnswered = optionalQs.filter(q => isQuestionAnswered(clusterId, q)).length;
    return {
      visible: visibleQs.length,
      required: requiredQs.length,
      answered,
      optionalAnswered,
      total: cl.questions.length,
    };
  };

  const getClusterProgress = (clusterId) => {
    const { required, answered } = getClusterCounts(clusterId);
    if (required === 0) return 100; // nothing required = nothing mandatory to fill
    return Math.round((answered / required) * 100);
  };

  // Global progression = ARITHMETIC MEAN of the per-cluster percentages.
  // The cluster percentage is the source of truth (it's what the startup
  // sees on the sidebar) ; the global is just its rounded average. With
  // this formula:
  //   - all clusters at 100%  ⇒  global is exactly 100% (no rounding gap)
  //   - each cluster has equal weight regardless of its question count
  //     (so 1 cluster at 0/5 doesn't get drowned by a cluster of 30 questions)
  //
  // totalQuestions / answeredCount are still computed (sum across clusters)
  // because the "soumettre" button title and the "Le questionnaire n'est
  // rempli qu'à X% (Y/Z questions)" message both rely on raw counts.
  let totalQuestions = 0;
  let answeredCount = 0;
  const clusterPercentages = clusters.map(([id]) => {
    const c = getClusterCounts(id);
    totalQuestions += c.required;
    answeredCount += c.answered;
    return getClusterProgress(id);
  });
  const globalProgress = clusterPercentages.length > 0
    ? Math.round(clusterPercentages.reduce((a, b) => a + b, 0) / clusterPercentages.length)
    : 0;

  // ----- render -----

  if (!definition) return <div className="loading">Chargement du questionnaire...</div>;

  if (submitted) return (
    <div style={{ padding: 60, textAlign: 'center' }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>&#10003;</div>
      <h2 style={{ color: '#27AE60' }}>Questionnaire soumis avec succès</h2>
      <p style={{ color: 'var(--text-light)', marginTop: 12 }}>Votre dossier est maintenant en cours d'analyse.</p>
    </div>
  );

  // Build skip-logic lookup for the current cluster (qid -> value)
  const qidLookup = buildQidLookup(responses, activeCluster, current);
  const visibleQuestions = current ? current.questions.filter(q => isVisible(q, qidLookup)) : [];

  // Whether the global submit can proceed
  const submitBlocked = !bpStatus.uploaded;

  return (
    <div style={{ padding: '32px 0' }}>
      <div className="container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h1 style={{ marginBottom: 4 }}>Questionnaire QIT {qitYear}</h1>
            <p style={{ color: 'var(--text-light)', fontSize: 14 }}>
              Questionnaire d'Information Transversale - Trophées de l'innovation 2026
              {viewMode && <span style={{ color: '#E67E22', fontWeight: 600 }}> (consultation {qitYear})</span>}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {!viewMode && (
              <>
                <button onClick={handleSave} className="btn btn-outline" disabled={saving || rawAnsweredCount === 0}
                  title={rawAnsweredCount === 0 ? 'Aucune question renseignée' : `${rawAnsweredCount} réponse(s) à sauvegarder`}>
                  {saving ? 'Sauvegarde...' : saved ? 'Sauvegardé !' : 'Sauvegarder'}
                </button>
                {startup?.trophy_participant && (
                  <button onClick={handleSubmit} className="btn btn-accent" disabled={answeredCount === 0 || submitBlocked}
                    title={submitBlocked ? 'Business Plan obligatoire dans la Data Room' :
                           answeredCount === 0 ? 'Questionnaire vide' :
                           globalProgress < 80 ? `Rempli à ${globalProgress}% — minimum 80% requis` : `Rempli à ${globalProgress}%`}>
                    Soumettre {globalProgress > 0 && globalProgress < 80 ? `(${globalProgress}%)` : ''}
                  </button>
                )}
              </>
            )}
            {qitSubmittedForYear && (
              <span className="badge" style={{ background: '#E8F5E9', color: '#27AE60', padding: '8px 16px', fontSize: 13 }}>
                ✓ Soumis pour {qitYear}
              </span>
            )}
          </div>
        </div>

        {/* Business Plan global banner — visible at all times */}
        {!viewMode && (
          <div className="card" style={{
            marginBottom: 20, padding: 16,
            background: bpStatus.uploaded ? '#E8F5E9' : '#FFEBEE',
            borderLeft: `4px solid ${bpStatus.uploaded ? '#27AE60' : '#C62828'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 24 }}>{bpStatus.uploaded ? '✓' : '!'}</span>
              <div>
                <strong style={{ color: bpStatus.uploaded ? '#1B5E20' : '#B71C1C' }}>
                  {bpStatus.uploaded ? 'Business Plan importé' : 'Business Plan à importer (obligatoire)'}
                </strong>
                <div style={{ fontSize: 12, color: 'var(--text-light)', marginTop: 2 }}>
                  {bpStatus.uploaded
                    ? `Fichier : ${bpStatus.file_name || 'document'}${bpStatus.uploaded_at ? ` — déposé le ${new Date(bpStatus.uploaded_at).toLocaleDateString('fr-FR')}` : ''}`
                    : 'Le Business Plan doit être déposé dans la Data Room (catégorie « Business Plan ») pour pouvoir soumettre le questionnaire.'}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Link to="/startup/dataroom" className="btn btn-primary" style={{ fontSize: 12 }}>
                {bpStatus.uploaded ? 'Remplacer dans la Data Room' : 'Aller à la Data Room'}
              </Link>
              <button onClick={refreshBpStatus} className="btn btn-outline" style={{ fontSize: 12 }}>
                Actualiser
              </button>
            </div>
          </div>
        )}

        {/* Year navigation */}
        {(availableYears.length > 0 || hasPreviousYear) && (
          <div className="card" style={{ marginBottom: 20, padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: 'var(--text-light)', fontWeight: 600 }}>Éditions :</span>
              {availableYears.map(y => {
                const yr = parseInt(y.year);
                const isActive = yr === qitYear;
                const isArchive = yr < CURRENT_YEAR;
                return (
                  <button key={yr} onClick={() => loadYear(yr, isArchive)}
                    className={`btn ${isActive ? 'btn-primary' : 'btn-outline'}`}
                    style={{ fontSize: 12, padding: '6px 14px' }}>
                    {yr} {isArchive && !isActive ? '(archive)' : ''} ({y.response_count} rép.)
                  </button>
                );
              })}
              {!hasCurrentYear && (
                <button onClick={startFreshQit} className={`btn ${qitYear === CURRENT_YEAR && !viewMode ? 'btn-primary' : 'btn-outline'}`}
                  style={{ fontSize: 12, padding: '6px 14px' }}>
                  {CURRENT_YEAR} (nouveau)
                </button>
              )}
            </div>
            {hasPreviousYear && qitYear === CURRENT_YEAR && !viewMode && (
              <button onClick={handlePrefill} className="btn btn-accent" disabled={prefilling}
                style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                {prefilling ? 'Pré-remplissage...' : `Pré-remplir ${CURRENT_YEAR} avec ${CURRENT_YEAR - 1}`}
              </button>
            )}
          </div>
        )}

        {qitSubmittedForYear && (
          <div className="card" style={{ marginBottom: 20, background: '#E8F5E9', borderLeft: '4px solid #27AE60', padding: 16 }}>
            <strong style={{ color: '#27AE60' }}>Questionnaire soumis pour {qitYear}</strong>
            <p style={{ fontSize: 13, color: 'var(--text-light)', margin: '4px 0 0' }}>
              Ce questionnaire a été soumis et est en lecture seule. Vos réponses sont en cours d'analyse par le jury.
            </p>
          </div>
        )}

        {viewMode && !qitSubmittedForYear && (
          <div className="card" style={{ marginBottom: 20, background: '#FFF8E1', borderLeft: '4px solid #F57F17', padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <strong style={{ color: '#F57F17' }}>Questionnaire {qitYear} (archivé)</strong>
                <p style={{ fontSize: 13, color: 'var(--text-light)', margin: '4px 0 0' }}>
                  Ce questionnaire est en lecture seule. Pour remplir le QIT {CURRENT_YEAR}, cliquez sur le bouton ci-contre.
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {hasPreviousYear && !hasCurrentYear && (
                  <button onClick={handlePrefill} className="btn btn-accent" disabled={prefilling}
                    style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                    {prefilling ? '...' : `Pré-remplir QIT ${CURRENT_YEAR}`}
                  </button>
                )}
                <button onClick={startFreshQit} className="btn btn-primary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                  QIT {CURRENT_YEAR} vierge
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="qit-layout">
          {/* Cluster sidebar */}
          <div className="card qit-cluster-sidebar" style={{ padding: 16, position: 'sticky', top: 80 }}>
            <h3 style={{ fontSize: 14, marginBottom: 12, color: 'var(--text-light)' }}>SECTIONS</h3>
            {clusters.map(([id, cl], i) => {
              const progress = getClusterProgress(id);
              const isIdent = id === 'CL-IDENT';
              return (
                <button key={id} onClick={() => setActiveCluster(id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 12px',
                    border: 'none', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13,
                    background: id === activeCluster ? '#E3F2FD' : 'transparent',
                    color: id === activeCluster ? 'var(--primary)' : 'var(--text)',
                    fontWeight: id === activeCluster ? 600 : 400, textAlign: 'left' }}>
                  <span style={{ width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0,
                    background: progress === 100 ? '#27AE60' : progress > 0 ? '#F39C12' : 'var(--border)',
                    color: progress > 0 ? 'white' : 'var(--text-light)' }}>
                    {progress === 100 ? '✓' : isIdent ? 'i' : i}
                  </span>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cl.name}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-light)' }}>{progress}%</span>
                </button>
              );
            })}
          </div>

          {/* Question pane */}
          <div className="card">
            <h2 style={{ fontSize: 20, marginBottom: 4 }}>{current.label}</h2>
            {(() => {
              const { visible, required, total } = getClusterCounts(activeCluster);
              const hidden = total - visible;
              const optionalCount = visible - required;
              return (
                <p style={{ color: 'var(--text-light)', fontSize: 13, marginBottom: 24 }}>
                  {hidden > 0 ? (
                    <span title={`${hidden} question(s) masquée(s) par le filtrage conditionnel (skip-logic)`}>
                      {visible}/{total} questions visibles
                    </span>
                  ) : (
                    <>{total} questions</>
                  )}
                  {optionalCount > 0 && (
                    <span title={`${optionalCount} question(s) "Commentaires éventuels" — facultative(s), non comptée(s) dans le %`}
                      style={{ marginLeft: 4, fontStyle: 'italic' }}>
                      (dont {optionalCount} facultative{optionalCount > 1 ? 's' : ''})
                    </span>
                  )}
                  {current.scored === false && ' — section non scorée (informations contextuelles)'}
                  {' '} - Progression : {getClusterProgress(activeCluster)}%
                </p>
              );
            })()}

            {visibleQuestions.map((q, idx) => (
              <QuestionRow
                key={q.id}
                clusterId={activeCluster}
                question={q}
                displayId={computeDisplayId(activeCluster, q.id, current.questions.indexOf(q), current.questions)}
                value={responses[`${activeCluster}_${q.id}`] || ''}
                onChange={handleChange}
                viewMode={viewMode}
                isLast={idx === visibleQuestions.length - 1}
                bpStatus={bpStatus}
              />
            ))}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
              <button onClick={() => setActiveCluster(clusters[currentIdx - 1]?.[0] || activeCluster)}
                className="btn btn-outline" disabled={currentIdx === 0}>
                Précédent
              </button>
              {/* Bouton Sauvegarder centré — sauve sans changer de section */}
              {!viewMode && (
                <button onClick={handleSave} className="btn btn-outline"
                  disabled={saving || rawAnsweredCount === 0}
                  title={rawAnsweredCount === 0 ? 'Aucune réponse à sauvegarder' : 'Sauvegarder vos réponses sur cette section sans changer de cluster'}>
                  {saving ? 'Sauvegarde...' : saved ? '✓ Sauvegardé' : 'Sauvegarder'}
                </button>
              )}
              {currentIdx < clusters.length - 1 ? (
                <button onClick={() => { if (!viewMode) handleSave(); setActiveCluster(clusters[currentIdx + 1][0]); }} className="btn btn-primary">
                  Suivant
                </button>
              ) : !viewMode && startup?.trophy_participant ? (
                <button onClick={handleSubmit} className="btn btn-accent" disabled={answeredCount === 0 || submitBlocked}
                  title={submitBlocked ? 'Business Plan obligatoire dans la Data Room' :
                         answeredCount === 0 ? 'Questionnaire vide' :
                         globalProgress < 80 ? `Rempli à ${globalProgress}% — minimum 80% requis` : `Rempli à ${globalProgress}%`}>
                  Soumettre le questionnaire {globalProgress > 0 && globalProgress < 80 ? `(${globalProgress}%)` : ''}
                </button>
              ) : !viewMode && !startup?.trophy_participant ? (
                <span style={{ fontSize: 13, color: 'var(--text-light)', fontStyle: 'italic' }}>
                  Participez aux Trophées pour soumettre le questionnaire
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Certification modal — opened by handleSubmit after pre-flight checks.
          Both branches (Refuser / Confirmer) are tracked server-side via
          startup_events for the admin audit trail. */}
      {certModalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="cert-modal-title"
          onClick={() => { if (!certBusy) setCertModalOpen(false); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: 16, padding: 28,
              maxWidth: 720, width: '100%', maxHeight: '90vh', overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
          >
            <h2 id="cert-modal-title" style={{ marginTop: 0, marginBottom: 14, color: '#2E75B6' }}>
              Certification de la candidature
            </h2>

            <p style={{ fontSize: 13, color: 'var(--text-light)', marginTop: 0, marginBottom: 16 }}>
              Avant la soumission définitive de votre questionnaire ISI {qitYear},
              merci de prendre connaissance de la clause ci-dessous et de
              confirmer votre engagement.
            </p>

            <label
              htmlFor="cert-checkbox"
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 12, padding: 16,
                border: certChecked ? '2px solid #2E75B6' : '2px solid var(--border)',
                borderRadius: 10, cursor: certBusy ? 'wait' : 'pointer',
                background: certChecked ? '#F0F7FF' : '#FAFBFC',
                marginBottom: 16,
                transition: 'border-color 0.15s, background 0.15s',
              }}
            >
              <input
                id="cert-checkbox"
                type="checkbox"
                checked={certChecked}
                disabled={certBusy}
                onChange={e => setCertChecked(e.target.checked)}
                style={{ marginTop: 4, width: 18, height: 18, flexShrink: 0, cursor: certBusy ? 'wait' : 'pointer' }}
              />
              <span style={{ fontSize: 13.5, lineHeight: 1.6, color: 'var(--text)' }}>
                Je certifie que les informations communiquées dans le présent
                dossier sont exactes, sincères et complètes à ma connaissance.
                <br /><br />
                Je reconnais que les informations fournies dans le cadre de
                cette candidature n'engagent que la startup candidate et/ou
                son représentant, et que l'organisateur ne saurait être tenu
                responsable d'éventuelles déclarations inexactes, incomplètes
                ou trompeuses.
                <br /><br />
                Je m'engage à informer l'organisateur de toute modification
                substantielle susceptible d'affecter l'éligibilité ou
                l'évaluation de la candidature.
              </span>
            </label>

            <div
              role="note"
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                background: '#FFF8E1', border: '1px solid #FFE082',
                borderRadius: 8, padding: 12, fontSize: 12.5,
                color: '#7A5A00', lineHeight: 1.55, marginBottom: 20,
              }}
            >
              <span aria-hidden="true" style={{ fontSize: 16, lineHeight: 1 }}>&#9432;</span>
              <span>
                Toute fausse déclaration, omission volontaire ou information
                trompeuse pourra entraîner la disqualification immédiate de la
                candidature, sans préjudice des voies de recours légales
                ouvertes à l'organisateur.
              </span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn-outline"
                disabled={certBusy}
                onClick={() => handleCertificationDecision(false)}
                style={{ fontSize: 14, padding: '10px 20px' }}
              >
                {certBusy ? '...' : 'Refuser et ne pas soumettre'}
              </button>
              <button
                type="button"
                className="btn btn-accent"
                disabled={certBusy || !certChecked}
                onClick={() => handleCertificationDecision(true)}
                style={{ fontSize: 14, padding: '10px 20px' }}
                title={!certChecked ? 'Cochez la case de certification pour activer ce bouton' : ''}
              >
                {certBusy ? 'Soumission en cours...' : 'Confirmer et soumettre'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// QuestionRow — renders one question, dispatches by type
// ---------------------------------------------------------------------------

function QuestionRow({ clusterId, question, displayId, value, onChange, viewMode, isLast, bpStatus }) {
  const q = question;
  const isPrefilled = !!q.prefill_from;
  const readonly = viewMode || (isPrefilled && q.type !== 'multiselect');

  return (
    <div style={{
      marginBottom: 24, paddingBottom: 24,
      borderBottom: isLast ? 'none' : '1px solid var(--border)',
      opacity: viewMode ? 0.85 : 1,
    }}>
      <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
        {displayId && (
          <span style={{ color: 'var(--primary-light)', marginRight: 8, fontFamily: 'monospace' }}>{displayId}</span>
        )}
        {q.text}
        {isPrefilled && (
          <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 500, color: '#1565C0',
            background: '#E3F2FD', padding: '2px 6px', borderRadius: 4 }}>
            Pré-rempli depuis le profil
          </span>
        )}
      </label>

      {q.help && (
        <p style={{ fontSize: 12, color: 'var(--text-light)', marginBottom: 8, fontStyle: 'italic' }}>{q.help}</p>
      )}

      {/* attachment_status: show BP banner inline */}
      {q.type === 'attachment_status' && (
        <div style={{
          padding: '12px 16px', borderRadius: 8, fontSize: 13,
          background: bpStatus?.uploaded ? '#E8F5E9' : '#FFF3E0',
          color: bpStatus?.uploaded ? '#1B5E20' : '#E65100',
          border: `1px solid ${bpStatus?.uploaded ? '#A5D6A7' : '#FFCC80'}`,
        }}>
          {bpStatus?.uploaded ? (
            <>
              <strong>✓ Business Plan importé.</strong>
              {bpStatus.file_name && <> Fichier : <em>{bpStatus.file_name}</em>.</>}
              {' '}<Link to="/startup/dataroom">Voir / remplacer dans la Data Room →</Link>
            </>
          ) : (
            <>
              <strong>! Business Plan à importer.</strong> Le fichier est obligatoire pour soumettre le questionnaire.
              {' '}<Link to="/startup/dataroom">Aller à la Data Room →</Link>
            </>
          )}
        </div>
      )}

      {/* select */}
      {q.type === 'select' && (() => {
        const hasExactMatch = value && q.options && q.options.includes(value);
        if (readonly && isPrefilled) {
          return (
            <div style={{ padding: '10px 14px', background: value ? '#E8F5E9' : '#F5F5F5',
              borderRadius: 8, maxWidth: 400, fontSize: 14,
              color: value ? '#2E7D32' : 'var(--text-light)', fontWeight: 500 }}>
              {value || 'Non renseigné'}
            </div>
          );
        }
        if (viewMode) {
          return (
            <div style={{ padding: '10px 14px', background: value ? '#E8F5E9' : '#F5F5F5',
              borderRadius: 8, maxWidth: 400, fontSize: 14,
              color: value ? '#2E7D32' : 'var(--text-light)', fontWeight: 500 }}>
              {value || 'Non renseigné'}
            </div>
          );
        }
        return (
          <select value={value || ''} onChange={e => onChange(clusterId, q.id, e.target.value)}
            className="form-input" style={{ maxWidth: 400 }}>
            <option value="">-- Sélectionner --</option>
            {q.options.map(o => <option key={o} value={o}>{o}</option>)}
            {value && !hasExactMatch && (
              <option value={value}>{value} (ancienne réponse)</option>
            )}
          </select>
        );
      })()}

      {/* text */}
      {q.type === 'text' && (
        viewMode || (isPrefilled && readonly) ? (
          <div style={{ padding: '10px 14px', background: value ? '#E8F5E9' : '#F5F5F5',
            borderRadius: 8, maxWidth: 600, fontSize: 14,
            color: value ? '#2E7D32' : 'var(--text-light)', fontWeight: 500 }}>
            {value || 'Non renseigné'}
          </div>
        ) : (
          <input type="text" value={value || ''} onChange={e => onChange(clusterId, q.id, e.target.value)}
            className="form-input" style={{ maxWidth: 600 }} placeholder="Votre réponse" />
        )
      )}

      {/* textarea */}
      {q.type === 'textarea' && (
        viewMode ? (
          <div style={{ padding: '10px 14px', background: value ? '#E8F5E9' : '#F5F5F5',
            borderRadius: 8, maxWidth: 800, fontSize: 14, whiteSpace: 'pre-wrap',
            color: value ? '#2E7D32' : 'var(--text-light)', fontWeight: 500 }}>
            {value || 'Non renseigné'}
          </div>
        ) : (
          <textarea value={value || ''} onChange={e => onChange(clusterId, q.id, e.target.value)}
            className="form-input" rows={3}
            style={{ maxWidth: 800, resize: 'vertical', fontFamily: 'inherit' }}
            placeholder="Votre réponse" />
        )
      )}

      {/* number */}
      {q.type === 'number' && (
        viewMode || (isPrefilled && readonly) ? (
          <div style={{ padding: '10px 14px', background: value ? '#E8F5E9' : '#F5F5F5',
            borderRadius: 8, maxWidth: 240, fontSize: 14,
            color: value ? '#2E7D32' : 'var(--text-light)', fontWeight: 500 }}>
            {value || 'Non renseigné'}
          </div>
        ) : (
          <input type="number" value={value || ''} onChange={e => onChange(clusterId, q.id, e.target.value)}
            className="form-input" style={{ maxWidth: 240 }} placeholder="Numérique" />
        )
      )}

      {/* multiselect */}
      {q.type === 'multiselect' && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {q.options.map(o => {
            const items = (value || '').split(',').map(s => s.trim()).filter(Boolean);
            const selected = items.includes(o);
            return (
              <label key={o} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
                border: `2px solid ${selected ? 'var(--primary-light)' : 'var(--border)'}`,
                borderRadius: 8, cursor: viewMode ? 'default' : 'pointer', fontSize: 13,
                background: selected ? '#E3F2FD' : 'white',
                opacity: viewMode ? 0.7 : 1 }}>
                <input type="checkbox" checked={selected} disabled={viewMode} onChange={() => {
                  if (viewMode) return;
                  const newItems = selected ? items.filter(i => i !== o) : [...items, o];
                  onChange(clusterId, q.id, newItems.join(','));
                }} style={{ display: 'none' }} />
                {o}
              </label>
            );
          })}
        </div>
      )}

      {/* multiselect_other */}
      {q.type === 'multiselect_other' && (() => {
        const { selected, other } = parseMultiselectOther(value);
        const updateOther = (newSelected, newOther) => {
          onChange(clusterId, q.id, joinMultiselectOther(newSelected, newOther));
        };
        return (
          <div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
              {q.options.map(o => {
                const isSel = selected.includes(o);
                return (
                  <label key={o} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
                    border: `2px solid ${isSel ? 'var(--primary-light)' : 'var(--border)'}`,
                    borderRadius: 8, cursor: viewMode ? 'default' : 'pointer', fontSize: 13,
                    background: isSel ? '#E3F2FD' : 'white',
                    opacity: viewMode ? 0.7 : 1 }}>
                    <input type="checkbox" checked={isSel} disabled={viewMode} onChange={() => {
                      if (viewMode) return;
                      const newSelected = isSel ? selected.filter(i => i !== o) : [...selected, o];
                      updateOther(newSelected, other);
                    }} style={{ display: 'none' }} />
                    {o}
                  </label>
                );
              })}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              <span style={{ fontSize: 12, color: 'var(--text-light)', minWidth: 90 }}>Autre (préciser) :</span>
              <input type="text" value={other} disabled={viewMode}
                onChange={e => updateOther(selected, e.target.value)}
                className="form-input" style={{ maxWidth: 360 }}
                placeholder="Autre option à préciser..." />
            </div>
          </div>
        );
      })()}
    </div>
  );
}
